/**
 * English UI strings — the source of truth every other language mirrors.
 * Content that lives in src/lib/data.js (ventures, varieties, articles …)
 * stays here untranslated; other languages override it via their `data` key.
 */
export default {
  common: {
    quote: "Request a Quote",
    downloadProfile: "Download Company Profile",
  },
  nav: {
    ventures: "Ventures",
    fruit: "Fruit Trading",
    rootstock: "Rootstock Import & Distribution",
    mango: "Mango Export",
    story: "Story",
    sourcing: "Sourcing",
    partners: "Partners",
    quality: "Quality",
    insights: "Insights",
    contact: "Contact",
    language: "Language",
  },
  hero: {
    lines: ["From Orchard", "to Export —", "Trading the Future", "of Farm Produce"],
    sub: "Farmsons connects growers across Himachal, Kashmir and India's mango belts directly to international buyers — bringing structure, quality standards and real grower relationships into one modern trading company.",
    partner: "Partner With Us",
  },
  marquee: {
    items: ["Apples", "Mangoes", "Rootstock", "Global Import", "Export-Grade", "Himachal", "Kashmir", "Malda"],
  },
  ventures: {
    overline: "What We Do",
    heading: "Three verticals. One operating discipline.",
    para: "A repeatable model built to add new verticals without a redesign — from trading today to import and export next.",
    explore: "Explore",
  },
  cine: { l1: "From orchard", l2pre: "to", l2accent: "export." },
  partnersGrid: {
    overline: "Partners",
    heading: "Trusted by partners across the supply chain.",
    visit: "Visit site",
  },
  home: {
    storyOverline: "Our Story",
    storyHeading: "Generational roots, meeting modern operating rigor.",
    storyLink: "Read our full story & meet the founder",
    ctaHeading: "Let's build a trade partnership.",
  },
  about: {
    overline: "Our Story",
    title: "Generational roots, meeting modern operating rigor.",
    lede: "A Kolkata-rooted trading house, entering its next chapter — bringing structure, quality standards and direct grower relationships together in one company.",
    founderOverline: "The Founder",
    founderHeading: "A different background, brought to a traditional trade.",
    exploreLink: "Explore what we do — Fruit Trading, Rootstock & Mango Export",
  },
  vertical: {
    overline: "What We Do",
    ctaHeading: "Interested in {venture}? Let's talk.",
    exploreOthers: "Explore other ventures:",
    sourcingLink: "Sourcing & Regions",
    appleOverline: "Apple Varieties",
    appleTitle: "Apples from Himachal Pradesh & Kashmir",
    appleIntro: "Grounded in the real cultivars we source — from Himachal's signature Royal Delicious to the tangy green Granny Smith and high-altitude Kinnaur apple.",
    mangoOverline: "Mango Varieties",
    mangoTitle: "Mangoes from South & East India",
    mangoIntro: "From the premium Alphonso and GI-tagged Kesar and Banganapalli to the fragrant Himsagar and Langra of Bengal's Malda belt.",
  },
  sourcing: {
    overline: "Sourcing & Regions",
    title: "From Himalayan slopes to the Gangetic delta — and eight import origins.",
    lede: "Every region we source from is chosen for a reason. Hover any node on the map to explore our global network of grower relationships and trade origins.",
    legendHQ: "Kolkata headquarters",
    legendOrigins: "Sourcing & trade origins — hover a node to explore.",
    importOverline: "Apple Import Origins",
    tradeLink: "See how we trade & import fruit",
  },
  map: { title: "Global Sourcing Network" },
  partnersPage: {
    overline: "Partners",
    title: "Trusted by partners across the supply chain.",
    lede: "From orchard inputs to distribution and export logistics, Farmsons collaborates with established names to deliver reliably at every stage.",
  },
  qualityPage: {
    overline: "Quality & Certifications",
    title: "Standards built for cross-border trust.",
    lede: "International buyers evaluate a trading partner on consistency. Four pillars underpin everything Farmsons ships — from a named grower to a distant port.",
  },
  qualityGrid: {
    overline: "Why it matters",
    para: "International buyers evaluate a trading partner on consistency. Our quality framework is structured to meet import specifications, compliance and traceability from day one.",
    badge: "Export-ready logistics",
  },
  insights: {
    overline: "Insights",
    title: "Notes from the orchard to the port.",
    lede: "Practical perspective on India's fruit trade — season outlooks, agronomy and export know-how for buyers and growers.",
    read: "Read insight",
  },
  article: {
    back: "All insights",
    cta: "Looking to source or partner with Farmsons?",
  },
  contact: {
    overline: "Contact / Request a Quote",
    title: "Let's build a trade partnership.",
    para: "Whether you're an international importer, an orchard grower, or a supply partner — tell us what you need and our team will respond directly.",
    address: "Kolkata, West Bengal, India",
    waPreface: "Prefer a quick chat? Reach us on WhatsApp.",
    profileTitle: "Company Profile",
    profileDesc: "A one-page brief on Farmsons — our verticals, sourcing regions and quality standards.",
  },
  form: {
    name: "Full Name",
    email: "Email",
    company: "Company",
    country: "Country",
    interest: "Produce Interest",
    message: "Message",
    namePh: "Your name",
    emailPh: "you@company.com",
    companyPh: "Company name",
    countryPh: "Country",
    interestPrompt: "Select an interest",
    messagePh: "Tell us about your requirement, volumes and timelines.",
    submit: "Send Inquiry",
    sending: "Sending...",
    incomplete: "Please complete all fields.",
    error: "Something went wrong. Please try again.",
    tooMany: "Too many requests. Please wait a moment and try again.",
    successTitle: "Inquiry received",
    successBody: "Thank you. A member of the Farmsons team will reach out shortly.",
    successAgain: "Send another inquiry",
    successToast: "Inquiry sent — our team will be in touch.",
  },
  wa: {
    chat: "Chat on WhatsApp",
    aria: "Chat with Farmsons on WhatsApp",
    message: "Hello Farmsons, I'd like to discuss a produce trade inquiry.",
  },
  rootstockGuide: {
    overline: "Rootstock Variety Guide",
    title: "Choose the right rootstock for your orchard.",
    intro: "Rootstock choice determines tree size, how soon an orchard bears fruit, its disease resistance and its suitability to local soil and climate. Farmsons helps Himachal and Kashmir growers match the right rootstock to the right site — not just supply it. Filter by series or vigor class below.",
    series: "Series",
    vigor: "Vigor",
    all: "All",
    bestFit: "Best fit",
    noMatch: "No rootstocks match that combination.",
  },
  unsub: {
    title: "Unsubscribe",
    processing: "Processing your request…",
    success: "You're unsubscribed",
    successBody: "You've been removed from our newsletter. Sorry to see you go.",
    invalid: "Invalid link",
    invalidBody: "This unsubscribe link is invalid or has already been used.",
    home: "Back to home",
  },
  footer: {
    blurb: "From orchard to export — a registered farm produce trading company building modern infrastructure between growers and international buyers. Est. 1985, Kolkata.",
    explore: "Explore",
    office: "Office",
    ourStory: "Our Story",
    city: "Kolkata, West Bengal",
    country: "India",
    rights: "All rights reserved.",
    tagline: "Trading the future of farm produce.",
    newsTitle: "Farmsons Newsletter",
    newsDesc: "Seasonal notes on India's fruit trade, straight to your inbox.",
    newsPlaceholder: "Your email address",
    newsButton: "Subscribe",
    newsSuccess: "Subscribed — welcome to the Farmsons newsletter.",
    newsError: "Could not subscribe. Please try again.",
  },
};
